/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Input } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React, { useEffect, useState } from 'react'
import BaseModal from 'src/components/ts-components/BaseModal'
import Button from 'src/components/ts-components/Button'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'
import Text from 'src/components/ts-components/Text'
import {
  font,
  paragraphSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'

export interface AvailabilityMessageModalProps {
  isOpen: boolean
  sending: boolean
  // Comma-separated names of the players this request is for. Empty when the
  // names couldn't be resolved — the row is then omitted entirely.
  playerNames?: string
  // SN-8309 — set once a send left players un-asked: names them, and the
  // button becomes Retry, which resends only their requests.
  error?: string | null
  onSend: (message: string) => void
  onClose: () => void
}

/**
 * Nested modal shown before sending an availability request. The official can
 * add an optional free-text message; sending with an empty field posts the
 * request with no message. One message applies to the whole batch, retries
 * included.
 */
const AvailabilityMessageModal: React.FC<AvailabilityMessageModalProps> = ({
  isOpen,
  sending,
  playerNames,
  error,
  onSend,
  onClose,
}) => {
  'use memo'
  const [message, setMessage] = useState('')

  // Reset the field each time the modal opens so a previous message never
  // carries into the next request.
  useEffect(() => {
    if (isOpen) {
      setMessage('')
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      // Level 2: opened from within the (level-1) Player Finder, so it needs a
      // higher layer for its mask to dim the finder rather than sit level with it.
      elevated={2}
      desktopWidth='480px'
      title={
        <Text variant='heading4' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords('Send Availability Request')}
        </Text>
      }
      actions={
        <div css={actionsStyle}>
          <Button
            text={error ? 'Retry' : 'Send Request'}
            variant='primary'
            isLoading={sending}
            onClick={() => onSend(message)}
          />
        </div>
      }
    >
      {playerNames ? (
        <div css={recipientsStyle}>
          <div css={recipientsLabelStyle}>{capitalizeWords('To')}:</div>
          <div css={recipientsNamesStyle}>{playerNames}</div>
        </div>
      ) : null}

      <Input.TextArea
        css={inputStyle}
        rows={4}
        value={message}
        placeholder='Add an optional message…'
        onChange={(e) => setMessage(e.target.value)}
      />

      <ErrorLabel
        fieldErrors={error ? { send: error } : {}}
        customError={{}}
        name='send'
      />
    </BaseModal>
  )
}

export default AvailabilityMessageModal

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
`

/* Recipient list above the message box, so the sender can confirm who the
   request goes to before posting. Label and names share one line; a long name
   list wraps under itself rather than pushing the label away. */
const recipientsStyle = css`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
`

const recipientsLabelStyle = css`
  ${font.body}
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_50};
  flex-shrink: 0;
`

const recipientsNamesStyle = css`
  ${font.body}
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.WHITE};
`

const inputStyle = css`
  resize: none;
`
